import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { CheckboxField } from '@openx/components-core/lib/CheckboxField/CheckboxField';
import { IndentedSections } from '@openx/components-core/lib/IndentedSection/IndentedSections';
import type { Option } from '@openx/types/options';
import { type ChangeEvent, useCallback, useMemo, useState } from 'react';
import type { FilterCriteria, MultiOptionWithRadioConfigurableFilter } from '../../types';
import { getInitialMultiOptionWithRangeValues } from '../../utils/multiOptionWithRange/getInitialMultiOptionWithRangeValues';
import { getSelectedOption } from '../../utils/typeGuards';
import { RadioSelector } from './RadioSelector';

export type MultiOptionWithRadioFilterProps<Criteria extends FilterCriteria> = {
  options: Option[];
  appliedFilters?: FilterCriteria;
  onOptionsChange: (key: string, value?: string) => void;
  config: MultiOptionWithRadioConfigurableFilter<Criteria>['config'];
};

export const MultiOptionWithRadioFilter = <Criteria extends FilterCriteria>({
  options,
  appliedFilters,
  onOptionsChange,
  config,
}: MultiOptionWithRadioFilterProps<Criteria>) => {
  const [checkedOptions, setCheckedOptions] = useState(getInitialMultiOptionWithRangeValues(options, appliedFilters));

  const filteredAvailableOptions = useMemo(() => {
    if (!config.removedAvailableOptions?.length) {
      return null;
    }

    const removedMap = config.removedAvailableOptions.reduce<Record<string, Set<string>>>(
      (acc, { optionId, removedOptionId }) => {
        if (!acc[optionId]) {
          acc[optionId] = new Set();
        }

        acc[optionId]!.add(removedOptionId);
        return acc;
      },
      {}
    );

    return Object.entries(removedMap).reduce<Record<string, Option[]>>((acc, [optionId, removedSet]) => {
      acc[optionId] = config.availableOptions.filter(availableOption => !removedSet.has(availableOption.id));
      return acc;
    }, {});
  }, [config.availableOptions, config.removedAvailableOptions]);

  const handleCheckboxChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { name, checked } = event.target;

      onOptionsChange(name, checked ? config.availableOptions[0].id : undefined);
      setCheckedOptions(prev => ({ ...prev, [name]: checked }));
    },
    [onOptionsChange, config.availableOptions]
  );

  return (
    <>
      {options.map(option => (
        <Box key={option.id}>
          <CheckboxField
            name={option.id}
            label={option.name}
            checked={checkedOptions[option.id] || false}
            onChange={handleCheckboxChange}
          />

          {checkedOptions[option.id] && config?.optionsWithRadio?.includes(option.id) && (
            <Stack
              direction="row"
              sx={{
                mb: 1,
              }}
            >
              <IndentedSections dataTest={`${config.radioOptionsLabel?.toLowerCase()}-subfilter`}>
                {config?.radioOptionsLabel && (
                  <Typography
                    variant="overline"
                    sx={{
                      display: 'block',
                    }}
                  >
                    {config.radioOptionsLabel.toUpperCase()}
                  </Typography>
                )}

                <RadioSelector
                  options={filteredAvailableOptions?.[option.id] ?? config.availableOptions}
                  selectedOptions={getSelectedOption(appliedFilters?.[option.id])}
                  onOptionChange={value => onOptionsChange(option.id, value)}
                  orientation="row"
                />
              </IndentedSections>
            </Stack>
          )}
        </Box>
      ))}
    </>
  );
};
